import React from 'react'
import { useTranslation } from 'react-i18next'

import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'

import { PanelContainer } from '../../../../components/Panel'
import { StatusTag } from '../../../../components/tableTemplates'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config'
import { useDeliveryForecastById } from '../../data'

type Props = {
  deliveryForecastId: string
  isConfirmationActive: boolean
}

export const DeliveryForecastInformation: React.FC<Props> = ({
  deliveryForecastId,
  isConfirmationActive,
}) => {
  const { i18n } = useTranslation()
  const { language } = i18n

  const { data, isLoading } = useDeliveryForecastById(deliveryForecastId)
  const { uiConfig: config } = useConfigContext()

  // ************************************************************
  // components
  // ************************************************************
  // scheduleAgreementNumber
  const scheduleAgreementNumberLabel = (
    <label className="text-sm" htmlFor="scheduleAgreementNumber"></label>
  )

  const scheduleAgreementNumberComponent = (
    <InputText
      id="scheduleAgreementNumber"
      value={data?.Payload.ScheduleAgreementNumber}
      className="w-full"
      disabled
    />
  )

  const scheduleAgreementNumberWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={scheduleAgreementNumberComponent}
      label={scheduleAgreementNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // currentReleaseDate
  const currentReleaseDateLabel = (
    <label className="text-sm" htmlFor="currentReleaseDate"></label>
  )

  const currentReleaseDateComponent = (
    <Calendar
      id="currentReleaseDate"
      value={new Date(data?.Payload.CurrentReleaseDate!)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const currentReleaseDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={currentReleaseDateComponent}
      label={currentReleaseDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // previousReleaseDate
  const previousReleaseDateLabel = (
    <label className="text-sm" htmlFor="previousReleaseDate"></label>
  )

  const previousReleaseDateComponent = (
    <div id="previousReleaseDate">
      {data?.Payload.PreviousReleaseDate ? (
        <Calendar
          id="previousReleaseDate"
          value={new Date(data?.Payload.PreviousReleaseDate!)}
          className="w-full"
          locale={language}
          disabled
        />
      ) : (
        <InputText
          id="previousReleaseDate"
          value=""
          className="w-full"
          disabled
        />
      )}
    </div>
  )

  const previousReleaseDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={previousReleaseDateComponent}
      label={previousReleaseDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationCode
  const confirmationCodeLabel = (
    <label className="text-sm w-full" htmlFor="confirmationCode"></label>
  )

  const confirmationCodeComponent = (
    <StatusTag
      id="confirmationCode"
      status={data?.Payload.ConfirmationCode}
      className="p-2"
    />
  )

  const confirmationCodeWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={confirmationCodeComponent}
      label={confirmationCodeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // AgreementType
  const agreementTypeLabel = (
    <label className="text-sm" htmlFor="agreementType"></label>
  )

  const agreementTypeComponent = (
    <InputText
      id="agreementType"
      value={data?.AgreementType}
      className="w-full"
      disabled
    />
  )

  const agreementTypeWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={agreementTypeComponent}
      label={agreementTypeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // currentReleaseNumber
  const currentReleaseNumberLabel = (
    <label className="text-sm" htmlFor="currentReleaseNumber"></label>
  )

  const currentReleaseNumberComponent = (
    <InputText
      id="currentReleaseNumber"
      value={data?.Payload.CurrentReleaseNumber}
      className="w-full"
      disabled
    />
  )

  const currentReleaseNumberWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={currentReleaseNumberComponent}
      label={currentReleaseNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // previousReleaseNumber
  const previousReleaseNumberLabel = (
    <label className="text-sm" htmlFor="previousReleaseNumber"></label>
  )

  const previousReleaseNumberComponent = (
    <InputText
      id="previousReleaseNumber"
      value={data?.Payload.PreviousReleaseNumber}
      className="w-full"
      disabled
    />
  )

  const previousReleaseNumberWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryForecastInformation}
      component={previousReleaseNumberComponent}
      label={previousReleaseNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      header={getConfigTranslation(
        config.deliveryForecast.deliveryForecastInformation.title,
      )}
      id="delivery-call-offs-delivery-forecast-details"
      visible={config.deliveryForecast.deliveryForecastInformation.enabled}
    >
      <div className="formgrid grid">
        {/* scheduleAgreementNumber */}
        {scheduleAgreementNumberWrapper}

        {/* currentReleaseDate */}
        {currentReleaseDateWrapper}

        {/* previousReleaseDate */}
        {previousReleaseDateWrapper}

        {/* confirmationCode */}
        {confirmationCodeWrapper}

        {/* agreementType */}
        {agreementTypeWrapper}

        {/* currentReleaseNumber */}
        {currentReleaseNumberWrapper}

        {/* previousReleaseNumber */}
        {previousReleaseNumberWrapper}
      </div>
    </PanelContainer>
  )
}
